import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { speakKarenNarration } from "../../utils/karenNarration";
import { captureKarenBriefing } from "../../utils/karenBriefing";
import { saveBriefingPlan, readSavedBriefingPlan } from "../../lib/briefingPlan";
import { formatConversationBudget, parkedConversationNotice } from "../../lib/parkedConversation.mjs";
import { closingWords, isOpeningDraftOutcomeUnknown, openingDraft, uploadOpening } from "../../lib/lessonOpening";

export default function LessonOpening(props) {
  // Account/session changes must discard all in-memory controls and abort the
  // old capture, even when the parent keeps this component mounted.
  return <ScopedLessonOpening key={JSON.stringify([props.ownerId, props.lesson.id, props.lesson.student_id])} {...props} />;
}

function ScopedLessonOpening({ ownerId, lesson, goal, level, support, adaptControl, onPlanSaved, onStart, onSkip, onExit, onSaveDeferred, onAnnouncementUnavailable, disabled = false, recoveryOnly = false }) {
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [farewell, setFarewell] = useState("");
  const [planChange, setPlanChange] = useState(null);
  const [conversationBudget, setConversationBudget] = useState(null);
  const [contextNotice, setContextNotice] = useState("");
  const pendingPlan = useRef(null);
  const [draft, setDraft] = useState(null);
  const currentDraft = useRef(null);
  const durableDraft = useRef(false);
  // A failed later write does not erase an older stored proposal. Track it
  // separately from whether the latest recording write was confirmed.
  const storedPlanProposal = useRef(false);
  const [localSaved, setLocalSaved] = useState(false);
  const [onlineSaved, setOnlineSaved] = useState(false);
  const onlineReceipt = useRef(false);
  const [storageOutcomeUnknown, setStorageOutcomeUnknown] = useState(false);
  const storageOutcome = useRef(null);
  const confirmedPlanReceipt = useRef(null);
  const planResolution = useRef(null);
  const [downloadOffered, setDownloadOffered] = useState(false);
  const controller = useRef(null);
  const stop = useRef(null);
  const locked = useRef(false);
  const continuing = useRef(false);
  const continueAfterRecording = useRef(true);
  const currentEnd = useRef(null);
  const dialog = useRef(null);
  const key = `${ownerId}:${lesson.id}`;
  const active = useRef(key);
  const currentStart = useRef(onStart);
  currentStart.current = onStart;
  const currentSkip = useRef(onSkip);
  currentSkip.current = onSkip;
  const skipAttempt = useRef(null);
  const microphoneOwner = value => window.dispatchEvent(new CustomEvent("ndg:lesson-opening-active", { detail: { active: value } }));
  const markStorageOutcomeUnknown = (error, intent) => {
    if (!isOpeningDraftOutcomeUnknown(error) || active.current !== key) return false;
    storageOutcome.current = { key, intent };
    setStorageOutcomeUnknown(true);
    const choices = intent.type === "plan-receipt" ? "Your lesson choices are saved online. " : "";
    const recording = onlineReceipt.current ? "Your recording is saved online, but its copy on this device could not be confirmed." : "The saved recording could not be confirmed.";
    setError(`${choices}${recording} Your recording and any agreed choices are still available on this screen. Check the saved copy before continuing or exiting.`);
    setPhase("error");
    return true;
  };
  const clearStorageOutcomeUnknown = () => {
    storageOutcome.current = null;
    setStorageOutcomeUnknown(false);
  };
  const publishSavedPlan = updated => {
    pendingPlan.current = null;
    setPlanChange(null);
    planResolution.current = null;
    confirmedPlanReceipt.current = null;
    onPlanSaved?.(updated);
  };

  useEffect(() => {
    if (recoveryOnly) return;
    // The browser's top layer covers the shell/header/chat and makes the rest
    // of the page inert. Do not change the pickup or teaching map layout.
    const element = dialog.current;
    element?.showModal();
    microphoneOwner(true);
    return () => { element?.close(); microphoneOwner(false); };
  }, [recoveryOnly]);

  useEffect(() => {
    active.current = key;
    let cancelled = false;
    openingDraft("get", key).then(saved => {
      if (cancelled) return;
      if (saved) { currentDraft.current = saved; durableDraft.current = true; storedPlanProposal.current = Boolean(saved.briefingPlan); pendingPlan.current = recoveryOnly ? null : saved.briefingPlan || null; setPlanChange(pendingPlan.current); setDraft(saved); setLocalSaved(true); setPhase("unsaved"); }
      else setPhase("idle");
    }).catch(() => { if (!cancelled) setPhase("idle"); });
    return () => { cancelled = true; active.current = null; controller.current?.abort(); microphoneOwner(false); };
  }, [key]);

  const persistPlan = async signal => {
    const change = pendingPlan.current;
    if (!change) return false;
    if (!onPlanSaved) throw new Error("The agreed choices cannot be saved here. Keep the saved plan or return to the briefing.");
    let updated = confirmedPlanReceipt.current?.change === change ? confirmedPlanReceipt.current.updated : null;
    if (!updated) {
      updated = await saveBriefingPlan(change, signal);
      if (signal.aborted || active.current !== key) return false;
      confirmedPlanReceipt.current = { change, updated };
    }
    if (signal.aborted || active.current !== key) return false;
    if (currentDraft.current && durableDraft.current) {
      // A recording may outlive its upload attempt and this screen. Persist
      // the confirmed choice receipt before clearing the pending proposal so
      // reopening cannot replay an already-saved goal over a newer plan.
      const savedDraft = { ...currentDraft.current, briefingPlan: null,
        briefingPlanReceipt: { lessonId: updated.id, pupilId: updated.student_id, revision: updated.goal_revision } };
      try {
        await openingDraft("put", key, savedDraft);
      } catch (failure) {
        if (signal.aborted || active.current !== key) return false;
        if (markStorageOutcomeUnknown(failure, { type: "plan-receipt", updated })) throw failure;
        throw new Error("Your choices are saved online, but the recording's local update is still pending. Please retry.");
      }
      if (signal.aborted || active.current !== key) return false;
      storedPlanProposal.current = false;
      currentDraft.current = savedDraft; setDraft(savedDraft); setLocalSaved(true);
    }
    publishSavedPlan(updated);
    return true;
  };

  const save = async (recording, signal) => {
    setPhase("saving");
    let durable = false;
    try {
      await openingDraft("put", key, recording);
      if (signal.aborted || active.current !== key) return;
      setLocalSaved(true);
      durableDraft.current = true;
      storedPlanProposal.current = Boolean(recording.briefingPlan);
      durable = true;
    } catch (failure) {
      if (signal.aborted || active.current !== key) return;
      if (markStorageOutcomeUnknown(failure, { type: "initial-save", recording })) throw failure;
      setLocalSaved(false);
      durableDraft.current = false;
    }
    const upload = new AbortController();
    const cancelUpload = () => upload.abort();
    signal.addEventListener("abort", cancelUpload, { once: true });
    let timer;
    try {
      await Promise.race([
        uploadOpening(recording, upload.signal),
        new Promise((_, reject) => { timer = setTimeout(() => {
          upload.abort(); reject(new Error("Recording upload timed out."));
        }, 12000); }),
      ]);
    } catch (failure) {
      if (signal.aborted || active.current !== key) return;
      // End is permission to continue, not proof of an online save. A durable
      // owner/session-scoped draft is the prerequisite for this recovery.
      if (!recoveryOnly && durable) {
        setPhase("saved");
        onSaveDeferred?.();
        if (continueAfterRecording.current) { locked.current = false; await continueLesson(); }
        return;
      }
      throw failure;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener("abort", cancelUpload);
    }
    if (signal.aborted || active.current !== key) return;
    onlineReceipt.current = true; setOnlineSaved(true);
    // The Briefing pack retries media only. It must never replay an old goal
    // proposal, especially after the lesson has ended or its plan has changed.
    const planSaved = recoveryOnly ? false : await persistPlan(signal);
    if (signal.aborted || active.current !== key) return;
    // Failed local cleanup must not re-upload or misreport the confirmed save.
    try {
      await openingDraft("delete", key);
    } catch (failure) {
      if (signal.aborted || active.current !== key) return;
      if (markStorageOutcomeUnknown(failure, { type: "cleanup", recording: currentDraft.current })) return;
    }
    // Cleanup may finish after this owner/session has left the screen.
    if (signal.aborted || active.current !== key) return;
    currentDraft.current = null; durableDraft.current = false; setDraft(null); setPhase("saved"); microphoneOwner(false);
    if (planSaved) { setPhase("review"); return; }
    if (continueAfterRecording.current) { locked.current = false; await continueLesson(); }
  };

  const begin = async () => {
    if (locked.current || continuing.current || storageOutcome.current || disabled || !ownerId || active.current !== key) return;
    locked.current = true;
    const control = new AbortController(); controller.current = control;
    setError("");
    try {
      if (draft) { await save(draft, control.signal); return; }
      onlineReceipt.current = false; setOnlineSaved(false);
      // The Briefing tap opens the browser’s real microphone permission prompt.
      // Karen’s opening then comes through the same live session as the recording.
      setPhase("permission"); setConversationBudget(null); setContextNotice(""); microphoneOwner(true);
      const result = await captureKarenBriefing({ signal: control.signal,
        ownerId, lessonId: lesson.id, pupilId: lesson.student_id,
        onContext: context => {
          if (active.current === key && !control.signal.aborted) setContextNotice(parkedConversationNotice(context));
        },
        onBudget: value => {
          if (active.current === key && !control.signal.aborted) setConversationBudget(value);
        },
        onPlanProposed: proposal => {
          if (active.current !== key || control.signal.aborted) throw new Error("This briefing has closed.");
          const change = proposal.goal === null && proposal.support_percent === null ? null : {
            ...proposal, lessonId: lesson.id, pupilId: lesson.student_id,
            expectedRevision: lesson.goal_revision || 0, expectedPlan: { ...(lesson.run_my_day_plan || {}), },
          };
          pendingPlan.current = change; setPlanChange(change);
        },
        onVoiceUnavailable: message => {
          if (active.current !== key || control.signal.aborted) return;
          setError(message);
          setPhase("error");
          microphoneOwner(false);
          control.abort();
        },
        onRecording: () => {
          if (active.current === key && controller.current === control && !control.signal.aborted) setPhase("conversation");
        },
        onStopReady: callback => {
          if (active.current === key && controller.current === control && !control.signal.aborted) stop.current = callback;
        },
        onEndRequested: action => {
          if (active.current === key && controller.current === control && !control.signal.aborted)
            currentEnd.current?.(action === "continue_lesson" ? "continue" : "stop");
        },
      });
      if (control.signal.aborted || active.current !== key) return;
      microphoneOwner(false);
      // Save only explicitly agreed lesson choices. The live mic stream itself
      // is never turned into a recording, local draft, or upload.
      const planSaved = await persistPlan(control.signal);
      if (control.signal.aborted || active.current !== key) return;
      if (planSaved) { setPhase("review"); return; }
      setPhase("saved");
      if (result?.action === "continue_lesson") { locked.current = false; await continueLesson(); }
    } catch (failure) {
      if (!control.signal.aborted && active.current === key && !storageOutcome.current) {
        setError(failure?.name === "NotAllowedError" ? "Microphone access was denied. Allow it in your browser or site settings, then tap Briefing to try again." : failure.message || "The briefing could not be completed.");
        setPhase("error"); microphoneOwner(false);
      }
    } finally { if (active.current === key && controller.current === control) locked.current = false; }
  };

  const continueLesson = async () => {
    if (disabled || continuing.current || storageOutcome.current || planResolution.current?.type === "keep-saved"
        || !ownerId || active.current !== key) return;
    if (currentDraft.current && !durableDraft.current && !downloadOffered && !onlineReceipt.current) return;
    continuing.current = true;
    controller.current?.abort(); locked.current = false; microphoneOwner(false);
    const control = new AbortController(); controller.current = control;
    setError(""); setFarewell(""); setPhase("starting");
    try {
      if (await persistPlan(control.signal)) { setPhase("review"); return; }
      if (control.signal.aborted || active.current !== key) return;
      const outcome = await currentStart.current(async route => {
      if (control.signal.aborted || active.current !== key) return false;
      const text = closingWords(route);
      setFarewell(text); setPhase("closing"); microphoneOwner(true);
      // Narration failure must not repeat the lesson-start request or pretend
      // that an unconfirmed destination was announced.
      const spoken = await speakKarenNarration(text, { signal: control.signal }).catch(() => false);
      if (control.signal.aborted || active.current !== key) return false;
      if (!spoken) onAnnouncementUnavailable?.(text);
      return true;
    }, { signal: control.signal });
      if (control.signal.aborted || active.current !== key) return;
      if (outcome?.status === "choices_changed") {
        // The page behind a modal is inert. Keep the refreshed saved plan and
        // optional adjustment in this attention owner, without recording twice.
        setFarewell(""); setPhase("review");
      } else if (outcome?.status === "error") {
        setError(outcome.message); setFarewell(""); setPhase("saved");
      } else {
        // The canonical parent normally leaves this screen after success.
        // If it stays mounted, keep a usable action without erasing review.
        setPhase("saved");
      }
    } catch (failure) {
      if (!control.signal.aborted && active.current === key && !storageOutcome.current) {
        setError(failure.message || "Could not start the lesson. Please retry.");
        setFarewell(""); setPhase("saved");
      }
    }
    finally { continuing.current = false; }
  };
  const keepSavedPlan = async () => {
    if (continuing.current || locked.current || storageOutcome.current) return;
    const control = new AbortController(); controller.current = control;
    const change = pendingPlan.current;
    if (!change) return;
    continuing.current = true;
    try {
      const saved = planResolution.current?.type === "keep-saved" && planResolution.current.change === change
        ? planResolution.current.saved
        : await readSavedBriefingPlan(change, control.signal);
      if (control.signal.aborted || active.current !== key) return;
      planResolution.current = { type: "keep-saved", change, saved };
      if (draft) {
        const kept = { ...draft, briefingPlan: null };
        try {
          await openingDraft("put", key, kept);
        } catch (failure) {
          if (control.signal.aborted || active.current !== key) return;
          if (markStorageOutcomeUnknown(failure, { type: "keep-saved", saved, recording: draft })) return;
          throw failure;
        }
        if (control.signal.aborted || active.current !== key) return;
        currentDraft.current = kept; durableDraft.current = true; storedPlanProposal.current = false; setDraft(kept); setLocalSaved(true);
      }
      if (active.current === key) {
        pendingPlan.current = null; setPlanChange(null); planResolution.current = null;
        onPlanSaved?.(saved, false); setError(""); setPhase("review");
      }
    } catch (failure) {
      if (!control.signal.aborted && active.current === key && !storageOutcome.current) {
        setError("Could not confirm the saved plan and update the local draft. Your agreed choices are still here. Please retry.");
        setPhase("saved");
      }
    } finally { continuing.current = false; }
  };
  const checkStorageOutcome = async () => {
    const pending = storageOutcome.current;
    if (!pending || pending.key !== key || disabled || locked.current || continuing.current || active.current !== key) return;
    continuing.current = true;
    controller.current?.abort();
    const control = new AbortController(); controller.current = control;
    setError("Checking the saved recording…"); setPhase("saving");
    try {
      // IndexedDB schedules this same-owner/session read behind an unfinished
      // write to the same object store, so it observes the write's final state.
      const saved = await openingDraft("get", key);
      if (control.signal.aborted || active.current !== key || storageOutcome.current !== pending) return;
      const recording = pending.intent.recording || currentDraft.current;
      if (saved && recording && (saved.mediaId !== recording.mediaId
          || saved.lessonId !== recording.lessonId || saved.pupilId !== recording.pupilId
          || saved.fingerprint !== recording.fingerprint)) {
        setError("A different recording is saved for this lesson. Your current recording remains on this screen. Download it to keep a copy. Check again after the other save has finished.");
        setPhase("error");
        return;
      }
      clearStorageOutcomeUnknown();
      const adoptSaved = value => {
        if (value) {
          currentDraft.current = value; durableDraft.current = true;
          storedPlanProposal.current = Boolean(value.briefingPlan);
          setDraft(value); setLocalSaved(true);
        } else {
          durableDraft.current = false; storedPlanProposal.current = false;
          setLocalSaved(false);
        }
      };
      const keepMemoryDraft = () => {
        if (recording) { currentDraft.current = recording; setDraft(recording); }
      };
      switch (pending.intent.type) {
        case "initial-save":
          if (saved) {
            adoptSaved(saved);
            if (saved.briefingPlan) { pendingPlan.current = saved.briefingPlan; setPlanChange(saved.briefingPlan); }
            else if (pendingPlan.current) setPlanChange(pendingPlan.current);
            setError("The saved recording is confirmed. Continue when ready; its upload can be retried from the Briefing pack.");
            setPhase("saved");
          } else {
            keepMemoryDraft(); adoptSaved(null);
            setError("No saved copy was found. This recording is still on this screen. Retry saving it or download it.");
            setPhase("error");
          }
          break;
        case "plan-receipt": {
          const { updated } = pending.intent;
          const receipt = saved?.briefingPlanReceipt;
          const receiptMatches = Boolean(saved && !saved.briefingPlan && receipt
            && receipt.lessonId === updated.id && receipt.pupilId === updated.student_id
            && receipt.revision === updated.goal_revision);
          if (saved) adoptSaved(saved);
          else { keepMemoryDraft(); adoptSaved(null); }
          if (receiptMatches || !saved) {
            publishSavedPlan(updated); setError(""); setPhase("review");
          } else {
            setError("Your lesson choices were saved online, but the recording still needs its saved-plan receipt. Retry Continue to confirm the recording update.");
            setPhase("saved");
          }
          break;
        }
        case "keep-saved":
          if (saved?.briefingPlan) {
            adoptSaved(saved);
            setError("The saved lesson plan is confirmed, but the recording still contains the pending choices. Retry Keep saved plan before leaving.");
            setPhase("saved");
          } else {
            if (saved) adoptSaved(saved);
            else { keepMemoryDraft(); adoptSaved(null); }
            pendingPlan.current = null; setPlanChange(null); planResolution.current = null;
            onPlanSaved?.(pending.intent.saved, false); setError(""); setPhase("review");
          }
          break;
        case "discard":
          if (saved?.briefingPlan) {
            adoptSaved(saved); pendingPlan.current = saved.briefingPlan; setPlanChange(saved.briefingPlan);
            setError("The saved recording still contains the agreed choices. They remain available here; retry discard or continue to save them.");
            setPhase("saved");
          } else {
            if (saved) adoptSaved(saved);
            else {
              const discarded = recording ? { ...recording, briefingPlan: null } : null;
              if (discarded) { currentDraft.current = discarded; setDraft(discarded); }
              adoptSaved(null);
            }
            pendingPlan.current = null; setPlanChange(null); planResolution.current = null;
            setPhase("saved"); microphoneOwner(false);
            if (saved || downloadOffered || onlineReceipt.current) onExit?.();
            else setError("No saved copy was found. Download the recording before leaving this screen.");
          }
          break;
        case "cleanup":
          if (saved) adoptSaved(saved);
          else { currentDraft.current = null; setDraft(null); adoptSaved(null); }
          setError("Your recording is saved online. The device copy has been checked."); setPhase("saved"); microphoneOwner(false);
          break;
        default:
          setError("The saved recording state could not be matched to this operation. Keep this screen open and try checking again.");
          storageOutcome.current = pending; setStorageOutcomeUnknown(true); setPhase("error");
      }
    } catch {
      if (control.signal.aborted || active.current !== key || storageOutcome.current !== pending) return;
      setError("The saved recording state is still unconfirmed. Your recording and choices remain on this screen. Check again before continuing or exiting.");
      setPhase("error");
    } finally { continuing.current = false; }
  };
  const endBriefing = (action = "continue") => {
    if (active.current !== key) return;
    continueAfterRecording.current = action === "continue";
    if (phase === "conversation") stop.current?.(action === "continue" ? "continue_lesson" : "stop_here");
    else if (["speaking", "permission"].includes(phase)) {
      // Cancel the pending mic/session request before handing off. Release the
      // capture lock so Continue can start; the aborted capture cannot save.
      controller.current?.abort(); locked.current = false; setPhase("saved");
      if (action === "continue") void continueLesson();
    }
  };
  currentEnd.current = endBriefing;
  const download = () => {
    const url = URL.createObjectURL(draft.blob);
    const link = document.createElement("a"); link.href = url;
    link.download = `lesson-opening.${draft.blob.type.includes("mp4") ? "m4a" : "webm"}`;
    link.click(); setDownloadOffered(true); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const activeBriefing = ["speaking", "permission", "conversation"].includes(phase);
  const canContinueLocally = Boolean(draft && localSaved && !recoveryOnly);
  const busy = ["loading", "starting", "closing"].includes(phase)
    || (phase === "saving" && !canContinueLocally);
  const canLeaveRecording = !draft || localSaved || downloadOffered || onlineSaved;
  const canExit = canLeaveRecording && !planChange && !storageOutcomeUnknown;
  const keepingSavedPlan = planResolution.current?.type === "keep-saved";
  const primaryContinues = ["saved", "review"].includes(phase) || canContinueLocally;
  const hasPlanDetails = Boolean(planChange) || phase === "review";
  const routeNeedsConfirmation = /not confirmed/i.test(farewell);
  const compactControl = activeBriefing || hasPlanDetails || Boolean(draft) || Boolean(error) || ["starting", "closing"].includes(phase);
  const exitBriefing = () => {
    if (!canExit || pendingPlan.current || storageOutcome.current) return;
    controller.current?.abort(); locked.current = false; microphoneOwner(false);
    onExit?.();
  };
  const skipBriefing = async () => {
    // Skip opts out of the live conversation. Retained audio belongs to the
    // separate legacy recovery flow and keeps all of its existing safeguards.
    if (!currentSkip.current || recoveryOnly || disabled || busy || continuing.current
        || skipAttempt.current || currentDraft.current || storageOutcome.current
        || !ownerId || active.current !== key) return;
    continuing.current = true;
    controller.current?.abort();
    stop.current = null;
    currentEnd.current = null;
    locked.current = false;
    microphoneOwner(false);
    // A live proposal is not a saved choice. Skip uses the canonical saved
    // plan and must never turn an abandoned conversation into a plan write.
    pendingPlan.current = null;
    planResolution.current = null;
    confirmedPlanReceipt.current = null;
    setPlanChange(null);
    const control = new AbortController();
    controller.current = control;
    const attempt = { control };
    skipAttempt.current = attempt;
    const current = () => active.current === key && controller.current === control
      && skipAttempt.current === attempt && !control.signal.aborted;
    setError(""); setFarewell(""); setPhase("starting");
    try {
      // The same parent writer owns preparation, route, booking and start.
      // No farewell callback means no microphone or narration on this path.
      const outcome = await currentSkip.current(null, { signal: control.signal });
      if (!current()) return;
      if (outcome?.status === "choices_changed") setPhase("review");
      else if (outcome?.status === "error") {
        setError(outcome.message || "Could not start the lesson. Please retry.");
        setPhase("error");
      } else setPhase("saved");
    } catch (failure) {
      if (current()) {
        setError(failure?.message || "Could not start the lesson. Please retry.");
        setPhase("error");
      }
    } finally {
      if (skipAttempt.current === attempt) {
        skipAttempt.current = null;
        continuing.current = false;
      }
    }
  };
  const discardPlanAndExit = async () => {
    if (!onExit || !canLeaveRecording || !pendingPlan.current || disabled
        || locked.current || continuing.current || storageOutcome.current || active.current !== key) return;
    controller.current?.abort();
    const control = new AbortController(); controller.current = control;
    continuing.current = true; setError(""); setPhase("saving");
    try {
      const recording = currentDraft.current;
      if (recording) {
        // An explicit discard must survive reopening the retained recording.
        // Do not change the lesson plan or silently replay this proposal later.
        const discarded = { ...recording, briefingPlan: null };
        if (storedPlanProposal.current) {
          await openingDraft("put", key, discarded);
          if (control.signal.aborted || active.current !== key) return;
          storedPlanProposal.current = false; durableDraft.current = true;
          setLocalSaved(true);
        }
        if (control.signal.aborted || active.current !== key) return;
        // With no stored proposal there is nothing to clear from storage.
        // The existing download safeguard still protects an in-memory blob.
        currentDraft.current = discarded; setDraft(discarded);
      }
      if (control.signal.aborted || active.current !== key) return;
      pendingPlan.current = null; setPlanChange(null); setPhase("saved");
      locked.current = false; microphoneOwner(false); onExit();
    } catch (failure) {
      if (!control.signal.aborted && active.current === key) {
        if (markStorageOutcomeUnknown(failure, { type: "discard", recording: currentDraft.current })) return;
        setError("Could not update the saved briefing. Your agreed choices are still here. Please retry.");
        setPhase("saved");
      }
    } finally { continuing.current = false; }
  };
  if (recoveryOnly && !draft) return null;
  const content = <section data-testid="lesson-opening" aria-live="polite" className={`grid min-w-0 w-full max-w-lg gap-4 ${hasPlanDetails ? "[@media(max-height:500px)]:max-w-3xl [@media(max-height:500px)]:grid-cols-2 [@media(max-height:500px)]:gap-3" : ""}`}>
    <button type="button" data-testid="start-lesson-btn" disabled={disabled || busy || storageOutcomeUnknown || (primaryContinues && (!canLeaveRecording || keepingSavedPlan))}
      aria-label={activeBriefing ? "End briefing or hang up with Karen" : undefined}
      onClick={activeBriefing ? () => endBriefing("stop") : ["saved", "review"].includes(phase) || canContinueLocally ? continueLesson : begin}
      className={`w-full self-start rounded-3xl bg-emerald-600 px-6 font-bold text-white disabled:opacity-60 ${compactControl ? "min-h-20 py-4 text-3xl" : "min-h-48 py-12 text-5xl"}`}>
      {activeBriefing ? (phase === "conversation" ? "End conversation" : "End") : phase === "closing" ? (routeNeedsConfirmation ? "Route needs confirmation" : "Ready to drive") : phase === "starting" ? "Starting lesson…" : phase === "loading" ? "Preparing…" : ["saved", "review"].includes(phase) || canContinueLocally ? "Continue to lesson" : phase === "saving" ? "Keeping recording…" : draft ? "Retry saving recording" : "Briefing"}
    </button>
    {!recoveryOnly && onSkip && !draft && <button type="button" data-testid="skip-briefing-btn"
      onClick={skipBriefing} disabled={disabled || busy || storageOutcomeUnknown}
      className="min-h-12 w-full rounded-xl border border-slate-600 px-4 py-3 text-sm font-semibold text-slate-200 hover:border-slate-400 disabled:opacity-50">
      {planChange ? "Skip — use saved plan and start lesson" : "Skip briefing — start lesson"}
    </button>}
    {!recoveryOnly && <div data-testid="briefing-conversation-budget" aria-live="off" className="rounded-xl border border-slate-600 px-3 py-2 text-center text-sm text-slate-200">
      <p>{formatConversationBudget(activeBriefing ? conversationBudget : null)}</p>
      {activeBriefing && conversationBudget?.phase === "wrap_up" && <p className="mt-1 text-amber-200">Finish the current answer, then end the conversation when ready.</p>}
      {activeBriefing && conversationBudget?.phase === "recap" && <p className="mt-1">Time to recap the goal and support together.</p>}
      {contextNotice && <p className="mt-1">{contextNotice}</p>}
    </div>}
    {hasPlanDetails && <div className="space-y-4 [@media(max-height:500px)]:col-start-2 [@media(max-height:500px)]:row-start-1 [@media(max-height:500px)]:row-span-3">
    {planChange && <div data-testid="opening-agreed-plan" role="status" className="rounded-xl border border-slate-600 bg-slate-800 p-3 text-sm">
      <p>Agreed goal: {planChange.goal || planChange.expectedPlan.desired_focus || goal}</p>
      <p>Support: {planChange.support_percent == null ? "keep saved choice" : `${planChange.support_percent}%`}</p>
      <p>{phase === "conversation" ? "End the conversation to save these choices." : storageOutcomeUnknown ? "Check the saved recording before making another choice." : keepingSavedPlan ? "Retry Keep saved plan to confirm the recording update." : confirmedPlanReceipt.current ? "These choices are saved online. Continue confirms the recording update." : "These choices are waiting to be saved. Continue saves them. To leave without them, choose Discard changes and exit."}</p>
      {error && !activeBriefing && <button type="button" data-testid="opening-keep-plan" onClick={keepSavedPlan} disabled={disabled || busy || storageOutcomeUnknown} className="min-h-11 underline">Keep saved plan</button>}
    </div>}
    {phase === "review" && <div data-testid="opening-updated-choices" role="status" className="space-y-2 rounded-xl border border-slate-600 bg-slate-800 p-4 text-sm">
      <p className="font-semibold">Lesson choices updated</p>
      <p>Goal: {goal || "Getting to know your driving"}</p>
      <p>Challenge: {level == null ? "Automatic — all levels 1–4" : `Level ${level}`}</p>
      <p>Support requested: {support == null ? "not stated" : `${support}%`}</p>
      <p>Discuss the updated plan while parked, then continue when ready.</p>
      {adaptControl}
    </div>}
    </div>}
    {(farewell || error || (!recoveryOnly && ["idle", "speaking", "permission", "conversation"].includes(phase))) && <div className="space-y-2">
    {farewell && <p className="text-center text-lg">{farewell}</p>}
    {!recoveryOnly && ["idle", "speaking", "permission", "recording"].includes(phase) && <p className="text-center text-sm text-slate-300">
      {phase === "conversation" ? "Live briefing with Karen. Say “we’re done, Karen” or “start the lesson now,” or tap End conversation." : phase === "speaking" ? "Karen is speaking." : phase === "permission" ? "Allow microphone access in the browser prompt to start Karen’s live briefing." : phase === "idle" ? "Parked safely, with your pupil? Tap to begin." : null}
    </p>}
    {error && <p role="alert" className="text-sm text-amber-200">{error}</p>}
    </div>}
    {storageOutcomeUnknown && <button type="button" data-testid="opening-storage-check" onClick={checkStorageOutcome} disabled={disabled || busy || phase === "saving"} className="min-h-11 rounded-lg border px-4">{phase === "saving" ? "Checking saved recording…" : "Check saved recording"}</button>}
    {(draft || (!recoveryOnly && onExit && !activeBriefing && phase !== "idle") || (!recoveryOnly && error && !busy && !activeBriefing)) && <div className="flex flex-col items-start gap-2">
    {draft && <div className="space-y-2 text-sm text-amber-200">
      <p>{storageOutcomeUnknown ? (onlineSaved ? "Recording saved online. The device copy is unconfirmed. You can download it from this screen." : "The device copy is unconfirmed. Keep this screen open or download the recording.") : onlineSaved ? "Recording saved online. You can also download a copy." : localSaved ? (recoveryOnly ? "Recording kept on this device until saved online. Retry saving it here." : "Recording kept on this device until saved online. Continue now; retry saving it later from this lesson's Briefing pack.") : "Recording is only held on this screen. Download it before leaving."}</p>
      <button type="button" data-testid="opening-download" onClick={download} className="min-h-11 rounded-lg border px-4">Download recording</button>
    </div>}
    {!recoveryOnly && !busy && !activeBriefing && !canContinueLocally && !["saved", "review"].includes(phase) && (error || draft) && <button type="button" data-testid="opening-continue" onClick={continueLesson} disabled={disabled || storageOutcomeUnknown || keepingSavedPlan || !canLeaveRecording} className="min-h-11 text-sm underline">
      {draft ? "Continue with local recording" : "Continue to lesson"}
    </button>}
    {!recoveryOnly && onExit && !activeBriefing && phase !== "idle" && <button type="button" data-testid="opening-exit" onClick={exitBriefing} disabled={!canExit} className="min-h-11 text-sm underline">Exit briefing</button>}
    {!recoveryOnly && onExit && !activeBriefing && planChange && <button type="button" data-testid="opening-discard-plan-exit" onClick={discardPlanAndExit} disabled={disabled || busy || storageOutcomeUnknown || phase === "saving" || !canLeaveRecording} className="min-h-11 text-sm underline">Discard changes and exit</button>}
    </div>}
  </section>;
  if (recoveryOnly) return content;
  return createPortal(<dialog ref={dialog} data-testid="briefing-screen" aria-label="Lesson briefing"
    onCancel={event => { event.preventDefault(); if (activeBriefing) endBriefing(); else exitBriefing(); }}
    style={{ position: "fixed", inset: 0, margin: 0, width: "100%", height: "100dvh", maxWidth: "none", maxHeight: "none", border: 0, padding: "max(24px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))", background: "#0f172a", color: "white" }}>
    <div className="flex min-h-full items-center justify-center">{content}</div>
  </dialog>, document.body);
}




